import type {
  LightType,
  TrussShape,
  StandSpec,
  Catalog,
  CatalogEntry,
  ConnectorType,
  PolarPattern,
  Port,
} from '../model/schema.js';
import { numberedPorts, port, powerIn } from './ports.js';

/**
 * Built-in equipment catalog (decision Q11). Port ids are stable per entry: items created from an
 * entry reuse them, so connections can reference e.g. `in5` on any SD16.
 *
 * Conventions used by validation and tracing:
 * - mic outputs that need +48V carry `phantom: true` (requirement); stagebox/console inputs carry
 *   `phantom: true` meaning "can provide +48V".
 * - stageboxes: inputs `in1..n`, outputs `out1..n`, digital trunk ports (`aes50a`, …).
 * - sub-cores: `in1..n` at stage end, `out1..n` at the fan-out end (pass-through by index).
 */

/** Accepts every common mains voltage (switch-mode PSU). */
const UNIVERSAL = [100, 120, 230, 240];
const MIC_OUT = port('out', 'Out', 'out', 'mic', 'xlr3');
const MIC_OUT_48V = port('out', 'Out', 'out', 'mic', 'xlr3', true);
const RF_OUT = port('rf', 'RF', 'out', 'wireless', 'wireless');
const RF_IN = port('rf', 'RF', 'in', 'wireless', 'wireless');

function mic(
  ref: string,
  brand: string,
  model: string,
  aliases: string[],
  pattern: PolarPattern,
  condenser = false,
  description?: string,
): CatalogEntry {
  const entry: CatalogEntry = {
    ref,
    kind: 'microphone',
    brand,
    model,
    aliases,
    pattern,
    ports: [condenser ? MIC_OUT_48V : MIC_OUT],
  };
  if (condenser) entry.phantom = true;
  if (description !== undefined) entry.description = description;
  return entry;
}

function wirelessMic(ref: string, brand: string, model: string, aliases: string[]): CatalogEntry {
  return {
    ref,
    kind: 'microphone',
    brand,
    model,
    aliases,
    pattern: 'cardioid',
    ports: [RF_OUT],
    description: 'Wireless handheld transmitter; pair it with its receiver via the RF port.',
  };
}

/**
 * Personal monitor mixer at the musician: network in + thru (daisy chain), headphone and stereo
 * line out, power from an adapter.
 */
function personalMixer(
  ref: string,
  brand: string,
  model: string,
  aliases: string[],
  network = 'Network',
): CatalogEntry {
  return {
    ref,
    kind: 'monitor_mixer',
    brand,
    model,
    aliases,
    ports: [
      port('net', `${network} In`, 'in', 'ethernet', 'ethercon'),
      port('thru', `${network} Thru`, 'out', 'ethernet', 'ethercon'),
      port('phones', 'Phones', 'out', 'line', 'trs'),
      port('outl', 'Line Out L', 'out', 'line', 'trs'),
      port('outr', 'Line Out R', 'out', 'line', 'trs'),
      powerIn('other', 'DC in'),
    ],
    power: { voltages: UNIVERSAL, watts: 15 },
    width: 0.3,
    depth: 0.22,
    description: `Personal monitor mixer (${network}); daisy-chain via Thru.`,
  };
}

/**
 * Generic lighting fixture (decision U12): power in (powerCON TRUE1), DMX in/out (XLR-5) when it
 * is DMX controlled. Conventional fixtures (profile, fresnel, follow spot) run on a dimmer.
 */
function light(
  ref: string,
  model: string,
  aliases: string[],
  type: LightType,
  watts: number,
  mode?: string,
): CatalogEntry {
  return {
    ref,
    kind: 'light',
    brand: 'Generic',
    model,
    aliases,
    ports: [
      powerIn('powercon_true1'),
      ...(mode === undefined
        ? []
        : [
            port('dmxin', 'DMX In', 'in', 'dmx', 'xlr5'),
            port('dmxout', 'DMX Out', 'out', 'dmx', 'xlr5'),
          ]),
    ],
    power: { voltages: UNIVERSAL, watts, plug: 'powercon_true1' },
    light: { type, ...(mode === undefined ? {} : { dmx: { mode } }) },
  };
}

/** Generic 29 cm 4-point truss segment; height and load are set per show. */
function truss(
  ref: string,
  model: string,
  aliases: string[],
  shape: TrussShape,
  width: number,
  depth = 0.29,
): CatalogEntry {
  return {
    ref,
    kind: 'truss',
    brand: 'Generic',
    model,
    aliases,
    ports: [],
    width,
    depth,
    truss: { shape, profile: '4pt', size: 0.29 },
  };
}

function furniture(
  ref: string,
  model: string,
  aliases: string[],
  type: string,
  width: number,
  depth: number,
): CatalogEntry {
  return {
    ref,
    kind: 'generic',
    brand: 'Generic',
    model,
    aliases,
    ports: [],
    width,
    depth,
    props: { furniture: type },
  };
}

function receiver(ref: string, brand: string, model: string, aliases: string[]): CatalogEntry {
  return {
    ref,
    kind: 'generic',
    brand,
    model,
    aliases,
    ports: [RF_IN, port('out', 'Out', 'out', 'mic', 'xlr3'), powerIn('iec')],
    power: { voltages: UNIVERSAL, watts: 15, plug: 'iec' },
    props: { passThrough: true },
    description: 'Wireless receiver; passes the RF input to the XLR output.',
  };
}

function di(
  ref: string,
  brand: string,
  model: string,
  aliases: string[],
  active: boolean,
  stereo = false,
): CatalogEntry {
  const sides = stereo ? [' L', ' R'] : [''];
  const suffix = (s: string): string => s.trim().toLowerCase();
  const ins = sides.map((s) => port(`in${suffix(s)}`, `In${s}`, 'in', 'instrument', 'ts'));
  const outs = sides.map((s) =>
    port(`out${suffix(s)}`, `Out${s}`, 'out', 'mic', 'xlr3', active ? true : undefined),
  );
  const thrus = sides.map((s) => port(`thru${suffix(s)}`, `Thru${s}`, 'out', 'instrument', 'ts'));
  const entry: CatalogEntry = {
    ref,
    kind: 'di',
    brand,
    model,
    aliases,
    ports: [...ins, ...outs, ...thrus],
    description: active ? 'Active DI (needs +48V phantom).' : 'Passive DI.',
  };
  if (active) entry.phantom = true;
  return entry;
}

/** Stereo DI fed by USB from a laptop (U73): USB in, Out L / Out R (bus powered, no +48V). */
function usbDi(ref: string, brand: string, model: string, aliases: string[]): CatalogEntry {
  const base = di(ref, brand, model, aliases, false, true);
  return {
    ...base,
    ports: [
      port('usb', 'USB', 'in', 'line', 'other'),
      ...base.ports.filter((p) => p.direction === 'out' && p.signal === 'mic'),
    ],
    description: 'Stereo USB DI for a laptop (bus powered).',
  };
}

function passiveSpeaker(
  ref: string,
  kind: 'wedge' | 'sidefill',
  brand: string,
  model: string,
  aliases: string[],
  width: number,
  depth: number,
): CatalogEntry {
  return {
    ref,
    kind,
    brand,
    model,
    aliases,
    ports: [
      port('in', 'In', 'in', 'speaker', 'speakon'),
      port('link', 'Link', 'out', 'speaker', 'speakon'),
    ],
    width,
    depth,
    description: 'Passive cabinet, needs an amplifier.',
  };
}

function activeSpeaker(
  ref: string,
  kind: 'wedge' | 'sidefill',
  model: string,
  aliases: string[],
  width: number,
  depth: number,
  watts: number,
): CatalogEntry {
  return {
    ref,
    kind,
    brand: 'Generic',
    model,
    aliases,
    ports: [
      port('in', 'In', 'in', 'line', 'xlr3'),
      port('link', 'Link', 'out', 'line', 'xlr3'),
      powerIn('powercon'),
    ],
    power: { voltages: UNIVERSAL, watts, plug: 'powercon' },
    width,
    depth,
    description: 'Active cabinet with XLR input and link.',
  };
}

function iemTransmitter(
  ref: string,
  brand: string,
  model: string,
  aliases: string[],
): CatalogEntry {
  return {
    ref,
    kind: 'iem',
    brand,
    model,
    aliases,
    ports: [
      port('inl', 'In L', 'in', 'line', 'xlr3'),
      port('inr', 'In R', 'in', 'line', 'xlr3'),
      RF_OUT,
      powerIn('iec'),
    ],
    power: { voltages: UNIVERSAL, watts: 20, plug: 'iec' },
    description: 'In-ear monitor transmitter (stereo).',
  };
}

function digitalStagebox(
  ref: string,
  brand: string,
  model: string,
  aliases: string[],
  inputs: number,
  outputs: number,
  watts: number,
): CatalogEntry {
  return {
    ref,
    kind: 'stagebox',
    brand,
    model,
    aliases,
    ports: [
      ...numberedPorts(inputs, 'in', 'In', 'in', 'mic', 'xlr3', true),
      ...numberedPorts(outputs, 'out', 'Out', 'out', 'line', 'xlr3'),
      port('aes50a', 'AES50 A', 'bi', 'aes', 'ethercon'),
      port('aes50b', 'AES50 B', 'bi', 'aes', 'ethercon'),
      powerIn('iec'),
    ],
    power: { voltages: UNIVERSAL, watts, plug: 'iec' },
    description: `${inputs} mic inputs, ${outputs} outputs, AES50.`,
  };
}

function subcore(channels: number): CatalogEntry {
  return {
    ref: `generic-subcore-${channels}`,
    kind: 'subcore',
    brand: 'Generic',
    model: `Sub-core ${channels}ch`,
    aliases: [`subcore${channels}`, `sub${channels}`],
    ports: [
      ...numberedPorts(channels, 'in', 'In', 'in', 'mic', 'xlr3'),
      ...numberedPorts(channels, 'out', 'Out', 'out', 'mic', 'xlr3'),
    ],
    description: `Analog ${channels}-channel sub-core with XLR fan-outs on both ends.`,
  };
}

function distro(
  ref: string,
  model: string,
  aliases: string[],
  inlet: ConnectorType,
  outlets: Port[],
  voltage: number,
): CatalogEntry {
  return {
    ref,
    kind: 'power_distro',
    brand: 'Generic',
    model,
    aliases,
    ports: [powerIn(inlet, 'Mains in'), ...outlets],
    power: { voltages: [voltage], plug: inlet },
  };
}

function riser(width: number, depth: number): CatalogEntry {
  return {
    ref: `generic-riser-${width}x${depth}`,
    kind: 'riser',
    brand: 'Generic',
    model: `Riser ${width}x${depth} m`,
    aliases: [`riser${width}x${depth}`, `podest${width}x${depth}`],
    ports: [],
    width,
    depth,
  };
}

function stand(ref: string, model: string, aliases: string[], spec: StandSpec): CatalogEntry {
  return { ref, kind: 'stand', brand: 'Generic', model, aliases, ports: [], stand: spec };
}

const schukoOut = (n: number): Port[] =>
  numberedPorts(n, 'out', 'Schuko', 'out', 'power', 'schuko');

export const BUILTIN_ENTRIES: readonly CatalogEntry[] = [
  // Dynamic and condenser microphones
  mic('shure-sm58', 'Shure', 'SM58', ['sm58', '58'], 'cardioid'),
  mic('shure-sm57', 'Shure', 'SM57', ['sm57', '57'], 'cardioid'),
  mic('shure-beta52a', 'Shure', 'Beta 52A', ['beta52a', 'b52', 'beta52'], 'supercardioid'),
  // common vocal / drum mics missing in the exploratory test (OIL-147)
  mic('shure-beta58a', 'Shure', 'Beta 58A', ['beta58a', 'b58', 'beta58'], 'supercardioid'),
  mic('shure-beta57a', 'Shure', 'Beta 57A', ['beta57a', 'b57', 'beta57'], 'supercardioid'),
  mic('sennheiser-e904', 'Sennheiser', 'e904', ['e904', '904'], 'cardioid'),
  mic(
    'sennheiser-e901',
    'Sennheiser',
    'e901',
    ['e901', '901'],
    'cardioid',
    true,
    'Boundary mic (kick drum).',
  ),
  mic(
    'shure-beta91a',
    'Shure',
    'Beta 91A',
    ['beta91a', 'b91', 'beta91'],
    'cardioid',
    true,
    'Boundary condenser (half-cardioid), kick drum inside.',
  ),
  mic(
    'shure-beta98',
    'Shure',
    'Beta 98AMP',
    ['beta98', 'b98', 'beta98amp'],
    'cardioid',
    true,
    'Clip-on drum condenser.',
  ),
  mic('shure-sm7b', 'Shure', 'SM7B', ['sm7b', 'sm7'], 'cardioid'),
  mic('shure-ksm9', 'Shure', 'KSM9', ['ksm9'], 'cardioid', true, 'Handheld vocal condenser.'),
  mic('sennheiser-e604', 'Sennheiser', 'e604', ['e604', '604'], 'cardioid'),
  mic('sennheiser-e606', 'Sennheiser', 'e606', ['e606', '606'], 'supercardioid'),
  mic('sennheiser-e906', 'Sennheiser', 'e906', ['e906', '906'], 'supercardioid'),
  mic('sennheiser-e935', 'Sennheiser', 'e935', ['e935', '935'], 'cardioid'),
  mic('sennheiser-md421', 'Sennheiser', 'MD 421', ['md421', '421', 'md421ii'], 'cardioid'),
  mic('sennheiser-e614', 'Sennheiser', 'e614', ['e614', '614'], 'supercardioid', true),
  mic(
    'akg-c414',
    'AKG',
    'C414',
    ['c414', '414'],
    'cardioid',
    true,
    'Multi-pattern large-diaphragm condenser.',
  ),
  mic('akg-c451', 'AKG', 'C451 B', ['c451', '451', 'c451b'], 'cardioid', true),
  mic('akg-d112', 'AKG', 'D112', ['d112', '112'], 'cardioid'),
  mic('neumann-km184', 'Neumann', 'KM 184', ['km184', '184'], 'cardioid', true),
  mic('neumann-kms105', 'Neumann', 'KMS 105', ['kms105', '105'], 'supercardioid', true),
  mic('audix-d6', 'Audix', 'D6', ['d6'], 'cardioid'),
  mic('audix-i5', 'Audix', 'i5', ['i5'], 'cardioid'),
  mic('ev-re20', 'Electro-Voice', 'RE20', ['re20'], 'cardioid'),
  mic('beyerdynamic-m88', 'Beyerdynamic', 'M 88 TG', ['m88', 'm88tg'], 'hypercardioid'),
  mic('beyerdynamic-m201', 'Beyerdynamic', 'M 201 TG', ['m201', 'm201tg'], 'hypercardioid'),
  mic('beyerdynamic-tgd70', 'Beyerdynamic', 'TG D70', ['tgd70', 'd70'], 'hypercardioid'),
  mic(
    'countryman-e6',
    'Countryman',
    'E6',
    ['e6', 'countryman'],
    'omni',
    true,
    'Headset condenser (XLR adapter needs +48V).',
  ),
  // Wireless
  wirelessMic('shure-ulxd2-sm58', 'Shure', 'ULXD2/SM58', ['ulxd2', 'ulxd2sm58']),
  receiver('shure-ulxd4', 'Shure', 'ULXD4', ['ulxd4']),
  wirelessMic('sennheiser-ewd-skm', 'Sennheiser', 'EW-D SKM-S', ['ewdskm', 'ewd']),
  receiver('sennheiser-ewd-em', 'Sennheiser', 'EW-D EM', ['ewdem']),
  // DI boxes
  di('radial-jdi', 'Radial', 'JDI', ['jdi'], false),
  di('radial-j48', 'Radial', 'J48', ['j48'], true),
  di('radial-prodi', 'Radial', 'ProDI', ['prodi'], false),
  di('radial-prod2', 'Radial', 'ProD2', ['prod2'], false, true),
  di('generic-di-passive', 'Generic', 'Passive DI', ['di', 'passivedi'], false),
  di('generic-di-active', 'Generic', 'Active DI', ['activedi'], true),
  // Stereo DIs (U73): two channels L / R
  di(
    'generic-di-stereo-passive',
    'Generic',
    'Stereo DI (passive)',
    ['stereodi', 'dualdi'],
    false,
    true,
  ),
  di('generic-di-stereo-active', 'Generic', 'Stereo DI (active)', ['activestereodi'], true, true),
  di('radial-jdi-stereo', 'Radial', 'JDI Stereo', ['jdistereo'], false, true),
  di('radial-j48-stereo', 'Radial', 'J48 Stereo', ['j48stereo'], true, true),
  di('klark-teknik-dn200', 'Klark Teknik', 'DN200', ['dn200'], true, true),
  usbDi('generic-di-usb', 'Generic', 'USB / laptop DI', ['usbdi', 'laptopdi']),
  usbDi('radial-usb-pro', 'Radial', 'USB-Pro', ['usbpro']),
  // Wedges
  passiveSpeaker('dnb-m4', 'wedge', 'd&b audiotechnik', 'M4', ['m4', 'dbm4'], 0.59, 0.43),
  passiveSpeaker('dnb-max2', 'wedge', 'd&b audiotechnik', 'MAX2', ['max2', 'dbmax2'], 0.55, 0.45),
  passiveSpeaker('lacoustics-x12', 'wedge', 'L-Acoustics', 'X12', ['x12'], 0.43, 0.5),
  passiveSpeaker('lacoustics-x15', 'wedge', 'L-Acoustics', 'X15 HiQ', ['x15', 'x15hiq'], 0.5, 0.52),
  activeSpeaker('generic-wedge-12', 'wedge', 'Wedge 12"', ['wedge12', 'wedge'], 0.4, 0.45, 400),
  activeSpeaker('generic-wedge-15', 'wedge', 'Wedge 15"', ['wedge15'], 0.5, 0.5, 600),
  // Sidefills
  passiveSpeaker('dnb-vsub', 'sidefill', 'd&b audiotechnik', 'V-SUB', ['vsub'], 0.72, 0.68),
  passiveSpeaker('dnb-y10p', 'sidefill', 'd&b audiotechnik', 'Y10P', ['y10p'], 0.3, 0.29),
  activeSpeaker('generic-sidefill', 'sidefill', 'Sidefill', ['sidefill'], 0.6, 0.6, 1000),
  // In-ear monitoring
  iemTransmitter('shure-psm1000', 'Shure', 'PSM1000 (P10T)', ['psm1000', 'p10t']),
  iemTransmitter('shure-psm300', 'Shure', 'PSM300 (P3T)', ['psm300', 'p3t']),
  iemTransmitter('sennheiser-ew-iem-g4', 'Sennheiser', 'ew IEM G4 (SR IEM G4)', [
    'ewiemg4',
    'ewiem',
  ]),
  {
    ref: 'generic-iem-bodypack',
    kind: 'iem',
    brand: 'Generic',
    model: 'IEM bodypack',
    aliases: ['bodypack', 'iempack'],
    ports: [RF_IN],
  },
  // Personal monitor mixers (OIL-53): fed over a network line from the console / hub
  personalMixer('behringer-p16m', 'Behringer', 'P16-M', ['p16', 'p16m', 'powerplay'], 'Ultranet'),
  personalMixer('behringer-p24', 'Behringer', 'P24', ['p24'], 'Ultranet'),
  personalMixer('aviom-a360', 'Aviom', 'A360', ['a360', 'aviom'], 'A-Net'),
  personalMixer('aviom-a16ii', 'Aviom', 'A-16II', ['a16', 'a16ii', 'aviom16'], 'A-Net'),
  personalMixer('allen-heath-me1', 'Allen & Heath', 'ME-1', ['me1'], 'ME'),
  personalMixer('generic-personal-mixer', 'Generic', 'Personal monitor mixer', [
    'personalmixer',
    'personalmonitor',
    'pmm',
  ]),
  // Stage boxes
  digitalStagebox('behringer-sd16', 'Behringer', 'SD16', ['sd16'], 16, 8, 40),
  digitalStagebox('behringer-s32', 'Behringer', 'S32', ['s32'], 32, 16, 60),
  digitalStagebox('midas-dl16', 'Midas', 'DL16', ['dl16'], 16, 8, 40),
  digitalStagebox('midas-dl32', 'Midas', 'DL32', ['dl32'], 32, 16, 60),
  digitalStagebox(
    'generic-stagebox-32-16',
    'Generic',
    '32/16 stagebox',
    ['stagebox3216', 'stagebox'],
    32,
    16,
    60,
  ),
  // Sub-cores
  subcore(8),
  subcore(12),
  subcore(16),
  // Power
  distro(
    'generic-schuko-6',
    'Schuko power strip 6-way',
    ['schuko6', 'schuko'],
    'schuko',
    schukoOut(6),
    230,
  ),
  distro('generic-cee16-distro', 'CEE16 distro (6x Schuko)', ['cee16'], 'cee16', schukoOut(6), 230),
  distro(
    'generic-cee32-distro',
    'CEE32 distro (2x CEE16, 6x Schuko)',
    ['cee32'],
    'cee32',
    [...numberedPorts(2, 'cee', 'CEE16', 'out', 'power', 'cee16'), ...schukoOut(6)],
    230,
  ),
  distro(
    'generic-us-strip-120',
    'US power strip 120 V (6-way)',
    ['usstrip', 'nema'],
    'nema5_15',
    numberedPorts(6, 'out', 'NEMA', 'out', 'power', 'nema5_15'),
    120,
  ),
  // Lighting (OIL-14): generic fixture types; DMX mode sets the footprint
  light(
    'generic-moving-spot',
    'Moving head spot',
    ['movingspot', 'spot', 'mh'],
    'moving_head_spot',
    450,
    '16ch',
  ),
  light(
    'generic-moving-wash',
    'Moving head wash',
    ['movingwash', 'wash'],
    'moving_head_wash',
    350,
    '16ch',
  ),
  light(
    'generic-moving-beam',
    'Moving head beam',
    ['movingbeam', 'beam'],
    'moving_head_beam',
    280,
    '16ch',
  ),
  light('generic-led-par', 'LED PAR', ['par', 'ledpar', 'par64'], 'par', 180, '8ch'),
  light('generic-led-bar', 'LED bar', ['ledbar', 'bar'], 'led_bar', 150, '12ch'),
  light('generic-led-wash', 'LED wash panel', ['ledwash', 'panel'], 'led_wash', 300, '10ch'),
  light(
    'generic-profile',
    'Profile spot (ellipsoidal)',
    ['profile', 'ellipsoidal', 'source4'],
    'profile',
    750,
  ),
  light('generic-fresnel', 'Fresnel 1 kW', ['fresnel', 'stufenlinse'], 'fresnel', 1000),
  light('generic-blinder', 'Blinder 2-lite', ['blinder', 'molefay'], 'blinder', 1300, '2ch'),
  light('generic-strobe', 'Strobe', ['strobe', 'stroboskop', 'atomic'], 'strobe', 1500, '2ch'),
  light('generic-follow-spot', 'Follow spot', ['followspot', 'verfolger'], 'follow_spot', 1200),
  // Truss (OIL-15): footprint = width × depth (straight: length, circle: diameter)
  truss('generic-truss-1m', 'Truss 1 m (29 cm, 4-point)', ['truss1', 'truss1m'], 'straight', 1),
  truss('generic-truss-2m', 'Truss 2 m (29 cm, 4-point)', ['truss2', 'truss2m'], 'straight', 2),
  truss(
    'generic-truss-3m',
    'Truss 3 m (29 cm, 4-point)',
    ['truss3', 'truss3m', 'truss'],
    'straight',
    3,
  ),
  truss(
    'generic-truss-corner',
    'Truss corner 90° (29 cm, 4-point)',
    ['trusscorner', 'corner'],
    'corner',
    0.5,
    0.5,
  ),
  truss(
    'generic-truss-circle-4m',
    'Truss circle Ø 4 m (29 cm, 4-point)',
    ['trusscircle', 'circle'],
    'circle',
    4,
    4,
  ),
  // Network
  {
    ref: 'generic-switch-8',
    kind: 'network_switch',
    brand: 'Generic',
    model: 'Network switch 8-port',
    aliases: ['switch8', 'switch'],
    ports: [...numberedPorts(8, 'p', 'Port', 'bi', 'ethernet', 'rj45'), powerIn('iec')],
    power: { voltages: UNIVERSAL, watts: 15, plug: 'iec' },
  },
  // Console
  {
    ref: 'generic-console',
    kind: 'console',
    brand: 'Generic',
    model: 'Digital console',
    aliases: ['console', 'desk'],
    ports: [
      ...numberedPorts(8, 'in', 'Local in', 'in', 'mic', 'xlr3', true),
      ...numberedPorts(8, 'out', 'Local out', 'out', 'line', 'xlr3'),
      port('aes50a', 'AES50 A', 'bi', 'aes', 'ethercon'),
      port('aes50b', 'AES50 B', 'bi', 'aes', 'ethercon'),
      powerIn('iec'),
    ],
    power: { voltages: UNIVERSAL, watts: 250, plug: 'iec' },
  },
  // Risers
  riser(2, 1),
  riser(2, 2),
  riser(1, 1),
  // Stage furniture (U72): a chair needs about 50 × 50 cm (seat ~45 × 45 cm)
  furniture(
    'generic-chair',
    'Chair (no armrests)',
    ['chair', 'stuhl', 'orchesterstuhl', 'orchestrachair'],
    'chair',
    0.5,
    0.5,
  ),
  furniture(
    'generic-chair-arms',
    'Chair with armrests',
    ['armchair', 'armlehnstuhl', 'stuhlmitarmlehnen'],
    'chair_arms',
    0.6,
    0.55,
  ),
  furniture(
    'generic-stool',
    'Stool (no backrest)',
    ['stool', 'hocker', 'drumhocker'],
    'stool',
    0.4,
    0.4,
  ),
  furniture('generic-bar-stool', 'Bar stool', ['barstool', 'barhocker'], 'bar_stool', 0.4, 0.4),
  furniture(
    'generic-music-stand',
    'Music stand',
    ['musicstand', 'notenpult', 'notenstaender', 'notenständer'],
    'music_stand',
    0.5,
    0.4,
  ),
  // Stands
  stand('generic-stand-boom-tall', 'Boom stand, tall', ['boom', 'boomtall', 'tallboom'], {
    type: 'telescopic_boom',
    base: 'tripod',
    minHeight: 0.9,
    maxHeight: 1.6,
    boomLength: 0.8,
  }),
  stand('generic-stand-boom-short', 'Boom stand, short', ['boomshort', 'shortboom'], {
    type: 'short_boom',
    base: 'tripod',
    minHeight: 0.4,
    maxHeight: 0.65,
    boomLength: 0.6,
  }),
  stand(
    'generic-stand-boom-lowprofile',
    'Boom stand, low-profile tripod',
    ['lowprofile', 'gravity'],
    {
      type: 'boom',
      base: 'low_profile_tripod',
      minHeight: 0.9,
      maxHeight: 1.6,
      boomLength: 0.8,
    },
  ),
  stand('generic-stand-straight', 'Straight stand, round base', ['straight', 'roundbase'], {
    type: 'straight',
    base: 'round',
    minHeight: 0.9,
    maxHeight: 1.6,
  }),
  stand('generic-stand-overhead', 'Overhead stand', ['overhead', 'ohstand'], {
    type: 'overhead',
    base: 'weighted',
    minHeight: 1.5,
    maxHeight: 3,
    boomLength: 1.5,
  }),
  stand('generic-speaker-stand', 'Speaker stand (35 mm)', ['speakerstand', 'boxenstativ'], {
    type: 'speaker_stand',
    base: 'tripod',
    minHeight: 1.2,
    maxHeight: 2,
  }),
  stand(
    'generic-lighting-stand',
    'Lighting stand with T-bar',
    ['lightstand', 'lichtstativ', 'tbar'],
    {
      type: 'lighting_stand',
      base: 'tripod',
      minHeight: 1.6,
      maxHeight: 3.5,
    },
  ),
  stand('generic-truss-lift', 'Truss lift (crank stand)', ['trusslift', 'lift', 'kurbelstativ'], {
    type: 'truss_lift',
    base: 'tripod',
    minHeight: 1.7,
    maxHeight: 5.3,
  }),
  stand('generic-stand-desk', 'Desk stand', ['deskstand', 'tablestand'], {
    type: 'desk',
    base: 'round',
  }),
  stand('generic-drum-clamp', 'Drum mic clamp', ['clamp', 'drumclamp'], {
    type: 'clamp',
    base: 'clamp',
  }),
];

/** The built-in catalog shipped with every workspace. */
export const BUILTIN_CATALOG: Catalog = { version: 1, entries: [...BUILTIN_ENTRIES] };
